/**
 * ResearchStep - Step 2 (Path B): Start keyword research
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AutoComplete } from 'antd';
import { Search, Plus, RefreshCw, Sparkles, AlertCircle } from 'lucide-react';
import { REGION_OPTIONS } from '@/types/contentPlanner';
import { useCreateTopicalMap } from '@/hooks/useContentPlannerAPI';
import { useTenantStore } from '@/stores/useTenantStore';
import { useBrandBible } from '@/hooks/useBrandBible';
import { contentPlannerService } from '@/services/contentPlannerService';
import { toast } from 'sonner';

interface ResearchStepProps {
  seedKeyword: string;
  region: string;
  onUpdateSeedKeyword: (keyword: string) => void;
  onUpdateRegion: (region: string) => void;
}

const ResearchStep: React.FC<ResearchStepProps> = ({
  seedKeyword,
  region,
  onUpdateSeedKeyword,
  onUpdateRegion,
}) => {
  const { currentTenant } = useTenantStore();
  const createMapMutation = useCreateTopicalMap();
  const { data: brandBible } = useBrandBible(currentTenant);

  const [error, setError] = useState('');
  const [isCreatingMap, setIsCreatingMap] = useState(false);

  // LLM suggestions state
  const [llmSuggestions, setLlmSuggestions] = useState<string[]>([]);
  const [isStreamingSuggestions, setIsStreamingSuggestions] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<string>('');
  const streamAbortControllerRef = useRef<AbortController | null>(null);

  // Handler to fetch LLM suggestions
  const handleGetSuggestions = useCallback(() => {
    if (!brandBible || isStreamingSuggestions) return;

    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
    }

    setIsStreamingSuggestions(true);
    setStreamingStatus('Starting...');

    const controller = contentPlannerService.streamKeywordSuggestions(
      {
        services_offered: brandBible.services_offered || [],
        customer_pain_points: brandBible.customer_pain_points || '',
        marketing_focus: brandBible.marketing_focus || '',
        ideal_client: brandBible.ideal_client || '',
        business_name: brandBible.business_name || currentTenant || '',
        exclude_existing: true,
      },
      {
        onStatus: (message) => setStreamingStatus(message),
        onKeywords: (keywords, count, excludedCount) => {
          setLlmSuggestions(keywords);
          setStreamingStatus(`Found ${count} keywords${excludedCount > 0 ? ` (${excludedCount} excluded)` : ''}`);
        },
        onError: (message) => {
          console.error('LLM suggestions stream error:', message);
          setStreamingStatus(`Error: ${message}`);
        },
        onComplete: () => setIsStreamingSuggestions(false),
      }
    );

    streamAbortControllerRef.current = controller;
  }, [brandBible, currentTenant, isStreamingSuggestions]);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Create autocomplete options
  const allSuggestionOptions = useMemo(() => {
    const options: { value: string; label: React.ReactNode }[] = [];

    // Add services from Brand Bible
    if (brandBible?.services_offered) {
      brandBible.services_offered.forEach((service: string) => {
        options.push({
          value: service,
          label: (
            <span className="flex items-center justify-between w-full">
              <span>{service}</span>
              <span className="text-xs text-blue-500 dark:text-blue-400 ml-2">service</span>
            </span>
          ),
        });
      });
    }

    // Add LLM suggestions
    llmSuggestions.forEach((suggestion) => {
      const exists = options.some(
        (opt) => opt.value.toLowerCase() === suggestion.toLowerCase()
      );
      if (!exists) {
        options.push({
          value: suggestion,
          label: (
            <span className="flex items-center justify-between w-full">
              <span>{suggestion}</span>
              <span className="text-xs text-green-500 dark:text-green-400 ml-2">suggested</span>
            </span>
          ),
        });
      }
    });

    return options;
  }, [brandBible, llmSuggestions]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!seedKeyword.trim()) return allSuggestionOptions;
    const search = seedKeyword.toLowerCase();
    return allSuggestionOptions.filter((opt) =>
      opt.value.toLowerCase().includes(search)
    );
  }, [seedKeyword, allSuggestionOptions]);

  const handleStartResearch = async () => {
    if (!seedKeyword.trim()) {
      setError('Please enter a seed keyword');
      return;
    }

    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    setIsCreatingMap(true);
    setError('');

    try {
      await createMapMutation.mutateAsync({
        tenantId: currentTenant,
        data: {
          seed_keyword: seedKeyword.trim(),
          region: region,
          language: 'en',
        },
      });

      toast.success('Keyword research started! This may take a few minutes.');
      // Note: Parent wizard will handle navigation to next step
    } catch (error) {
      console.error('Failed to create topical map:', error);
      toast.error('Failed to start keyword research. Please try again.');
      setError('Failed to start research. Please try again.');
    } finally {
      setIsCreatingMap(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Start keyword research</h2>
        <p className="text-sm text-muted-foreground">
          Enter a seed keyword and we'll fetch related keywords from Ahrefs, then group them into topic clusters.
        </p>
      </div>

      <div className="space-y-4">
        {/* Seed Keyword Input */}
        <div>
          <Label htmlFor="seedKeyword" className="mb-2 flex items-center gap-2">
            <Search className="h-4 w-4" />
            Seed Keyword
          </Label>
          <AutoComplete
            id="seedKeyword"
            placeholder="e.g., 'your main service'"
            value={seedKeyword}
            onChange={(value) => {
              onUpdateSeedKeyword(value);
              setError('');
            }}
            onSelect={(value) => onUpdateSeedKeyword(value)}
            options={filteredOptions}
            onKeyDown={(e) => e.key === 'Enter' && handleStartResearch()}
            className="w-full"
            style={{ width: '100%' }}
            size="large"
            status={error ? 'error' : undefined}
          />
          {error && (
            <p className="text-sm text-destructive mt-2 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {error}
            </p>
          )}
        </div>

        {/* Region Selection */}
        <div>
          <Label className="mb-2 block">Region</Label>
          <Select value={region} onValueChange={onUpdateRegion}>
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REGION_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  <span className="flex items-center gap-2">
                    <span>{r.flag}</span>
                    <span>{r.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* AI Suggestions Status */}
        {isStreamingSuggestions && (
          <div className="p-3 bg-teal-light border border-teal/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-teal animate-pulse" />
              <span className="text-sm font-medium text-teal-dark">{streamingStatus}</span>
            </div>
          </div>
        )}

        {/* AI Suggestions Button */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              {!isStreamingSuggestions && allSuggestionOptions.length > 0 && (
                <span className="text-green-600 dark:text-green-400 font-medium mr-1">
                  {allSuggestionOptions.length} suggestions available.
                </span>
              )}
              Get AI-powered keyword suggestions based on your brand profile.
            </p>
          </div>
          {brandBible && !isStreamingSuggestions && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGetSuggestions}
              className="animate-ai-suggest ml-3 shrink-0"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {llmSuggestions.length > 0 ? 'Refresh' : 'Get Suggestions'}
            </Button>
          )}
        </div>

        {/* Start Research Button */}
        <Button
          onClick={handleStartResearch}
          disabled={isCreatingMap || !seedKeyword.trim() || !currentTenant}
          className="w-full h-11"
          size="lg"
        >
          {isCreatingMap ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Starting Research...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              Start Keyword Research
            </>
          )}
        </Button>
      </div>

      <div className="text-xs text-center text-muted-foreground pt-2">
        Research typically takes 2-3 minutes to complete
      </div>
    </div>
  );
};

export default ResearchStep;
